import type { BillingCycle, Plan } from "../types";

/**
 * The marketing website deep-links into the app sign-up with a plan flag, e.g.
 * `/sign-up?plan=maestro&billing=annual`. It's only a hint — everyone starts on
 * the free trial; the flag pre-selects the plan + cycle on the Upgrade page later.
 *
 * Clerk drops the query string when it redirects after sign-up, so we stash the
 * flag in sessionStorage as soon as the app loads with it present, and consume it
 * on the Upgrade page.
 */
const KEY = "ciaotutor_intended_plan";

export type CheckoutFlag = { plan: Exclude<Plan, "trial">; billing: BillingCycle };

const VALID_PLANS = ["pratica", "maestro"] as const;
const VALID_CYCLES = ["monthly", "annual"] as const;

/** Read `?plan=&billing=` from the current URL and persist it (no-op if absent). */
export function captureCheckoutFlagFromUrl(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const plan = params.get("plan");
    if (!plan || !VALID_PLANS.includes(plan as never)) return;
    const billingRaw = params.get("billing");
    const billing = VALID_CYCLES.includes(billingRaw as never)
      ? (billingRaw as BillingCycle)
      : "annual";
    sessionStorage.setItem(KEY, JSON.stringify({ plan, billing }));
  } catch {
    /* sessionStorage unavailable — ignore */
  }
}

/** Return the stashed flag without clearing it (Upgrade page pre-selection). */
export function readCheckoutFlag(): CheckoutFlag | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as CheckoutFlag) : null;
  } catch {
    return null;
  }
}

export function clearCheckoutFlag(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
